import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/timeago";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const REFRESH_MS = 20000;

export default function NotificationBell() {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=10");
      setRows(data.rows || []);
      setUnread(data.unread_count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch {
      /* silent */
    }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch {
      /* silent */
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        data-testid="notification-bell"
      >
        <button
          type="button"
          aria-label="Notifications"
          className="relative rounded-md p-2 text-slate-500 outline-none transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-cargodash-primary-500"
        >
          <Bell className="h-4.5 w-4.5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-cargodash-action px-1 text-[10px] font-semibold text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <div className="font-display text-sm font-semibold text-slate-900">
            Notifications
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-cargodash-primary-700 hover:underline"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notifications-dropdown-list">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-6 text-center text-slate-400">
              <Inbox className="h-5 w-5" />
              <div className="text-xs">You're all caught up.</div>
            </div>
          ) : (
            rows.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  if (!n.read) markOne(n.id);
                  if (n.link) window.location.assign(n.link);
                }}
                data-testid={`notification-item-${n.id}`}
                className={`flex w-full flex-col items-start gap-0.5 border-b border-slate-100 px-4 py-2.5 text-left text-sm last:border-b-0 hover:bg-slate-50 ${
                  n.read ? "opacity-70" : ""
                }`}
              >
                <div className="flex w-full items-center gap-2">
                  {!n.read && (
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-cargodash-primary-600" />
                  )}
                  <span className="truncate font-medium text-slate-900">{n.title}</span>
                </div>
                {n.body && (
                  <div className="line-clamp-2 text-xs text-slate-500">{n.body}</div>
                )}
                <div className="text-[10px] text-slate-400">{timeAgo(n.ts)}</div>
              </button>
            ))
          )}
        </div>
        <div className="border-t border-slate-200 px-4 py-2">
          <Link
            to="/notifications"
            data-testid="notifications-view-all"
            className="block text-center text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            View all
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
